import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  CheckCircle2,
  Clock,
  ExternalLink,
  Loader2,
  Lock,
  MapPin,
  Package,
  PartyPopper,
  ReceiptIndianRupee,
  RotateCcw,
  Star,
  Store,
  Truck,
  XCircle,
} from 'lucide-react';
import { ShopChrome, Crumbs, SignInPrompt } from '@/components/shop/ShopChrome';
import { StatusPill } from '@/components/shop/ShopUI';
import { CancelSheet, ReturnSheet, ReviewSheet } from '@/components/shop/OrderActions';
import DeliveryCard from '@/components/shop/DeliveryCard';
import { EmptyState, Img, Shimmer } from '@/components/ui';
import { cancelReturn, cancelUnpaidOrder, getOrder, getOrderCheckout } from '@/api/shop';
import { useRazorpay } from '@/services/paymentService';
import { payShopOrder } from '@/lib/shopPayment';
import { refreshCartCount } from '@/lib/shopStore';
import { useAuth } from '@/context/AuthContext';
import { friendlyError } from '@/lib/apiError';
import {
  CANCELLABLE_SHIPMENT,
  ORDER_LABELS,
  RETURN_LABELS,
  RETURN_REASONS,
  SHIPMENT_LABELS,
  SHIPMENT_STEPS,
  activeQty,
  istDate,
  istDateTime,
  istDay,
  rupees,
  shipmentTone,
} from '@/lib/shop';

/* --------------------------------------------------------------- tracker */

const ShipmentTracker = ({ shipment }) => {
  const { status } = shipment;
  if (status === 'cancelled' || status === 'returned_to_origin') {
    return (
      <div className="flex items-start gap-3 rounded-2xl bg-danger-tint p-4 text-sm text-danger">
        <XCircle className="mt-0.5 h-5 w-5 shrink-0" />
        <div>
          <p className="font-semibold">{status === 'cancelled' ? 'Shipment cancelled' : 'Returned to the seller'}</p>
          {shipment.cancelledAt && <p className="text-xs">on {istDateTime(shipment.cancelledAt)}</p>}
          {shipment.cancelReason && <p className="mt-0.5 text-xs text-danger/80">{shipment.cancelReason}</p>}
        </div>
      </div>
    );
  }
  const current = Math.max(0, SHIPMENT_STEPS.findIndex((s) => s.key === status));
  const dates = { shipped: shipment.shippedAt, delivered: shipment.deliveredAt };
  return (
    <ol className="relative flex items-start justify-between" aria-label="Shipment progress">
      {SHIPMENT_STEPS.map((step, i) => {
        const done = i <= current;
        return (
          <li key={step.key} className="relative flex flex-1 flex-col items-center text-center" aria-current={i === current ? 'step' : undefined}>
            {i > 0 && (
              <span className={`absolute right-1/2 top-[11px] h-0.5 w-full -translate-y-1/2 ${i <= current ? 'bg-success' : 'bg-ink-200'}`} aria-hidden="true" />
            )}
            <span
              className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                done ? 'border-success bg-success text-white' : 'border-ink-200 bg-white'
              } ${i === current && status !== 'delivered' ? 'ring-4 ring-success/20' : ''}`}
            >
              {done && <CheckCircle2 className="h-4 w-4" />}
            </span>
            <span className={`mt-1.5 text-[10px] leading-tight sm:text-xs ${i === current ? 'font-semibold text-ink-900' : done ? 'text-ink-700' : 'text-ink-400'}`}>
              {step.label}
            </span>
            {dates[step.key] && <span className="hidden text-[10px] text-ink-500 sm:block">{istDay(dates[step.key])}</span>}
          </li>
        );
      })}
    </ol>
  );
};

/* ------------------------------------------------------------ countdown */

/** "14:59" until `iso`; calls onEnd once when it reaches zero. */
const useCountdown = (iso, onEnd) => {
  const end = iso ? new Date(iso).getTime() : NaN;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!Number.isFinite(end)) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [end]);
  const left = Number.isFinite(end) ? Math.max(0, end - now) : null;
  const ended = left === 0;
  useEffect(() => {
    if (ended) onEnd?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ended]);
  if (left == null) return null;
  const secs = Math.ceil(left / 1000);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return { left, label: `${h ? `${h}:${String(m).padStart(2, '0')}` : m}:${String(s).padStart(2, '0')}` };
};

/* ----------------------------------------------------------------- page */

const reasonLabel = (v) => RETURN_REASONS.find((r) => r.value === v)?.label || v;

const ShopOrderDetail = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const placed = params.get('placed') === '1';
  const { user, openAuthModal } = useAuth();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [cancelFor, setCancelFor] = useState(null); // shipment
  const [returnFor, setReturnFor] = useState(null); // item
  const [reviewFor, setReviewFor] = useState(null); // item
  const [paying, setPaying] = useState(false);
  const [notPayable, setNotPayable] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [undoingReturn, setUndoingReturn] = useState(null);
  const navigate = useNavigate();
  const { initiatePayment } = useRazorpay();

  const load = useCallback(() => {
    getOrder(id)
      .then(setOrder)
      .catch((err) => setError({ status: err?.response?.status, message: friendlyError(err, 'This order could not be loaded') }));
  }, [id]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  // The hold on an unpaid order runs out at expiresAt; reload then so the
  // page shows the expired state instead of a dead "Complete payment".
  const countdown = useCountdown(order?.status === 'pending_payment' ? order.expiresAt : null, () => setTimeout(load, 1500));

  if (!user) {
    return (
      <ShopChrome>
        <SignInPrompt icon={Package} title="Log in to see this order" onLogin={openAuthModal} />
      </ShopChrome>
    );
  }

  if (error) {
    return (
      <ShopChrome>
        <div className="lk-container py-12">
          <EmptyState
            icon={Package}
            title={error.status === 404 ? 'Order not found' : 'Something went wrong'}
            message={error.message}
            action={<Link to="/shop/orders" className="lk-btn-primary">My orders</Link>}
          />
        </div>
      </ShopChrome>
    );
  }

  if (!order) {
    return (
      <ShopChrome>
        <div className="lk-container max-w-4xl space-y-4 py-8">
          <Shimmer className="h-24 w-full rounded-2xl" />
          <Shimmer className="h-72 w-full rounded-2xl" />
        </div>
      </ShopChrome>
    );
  }

  const shipments = order.details?.shipments || [];
  const address = order.details?.address || order.quote?.address;
  const canCancelOrder = ['confirmed', 'partially_cancelled'].includes(order.status);
  const fees = (order.parties || []).reduce((s, p) => s + Number(p.fees || 0), 0);
  const refunds = order.refunds || [];
  const summary = order.quote?.summary;

  // An order left at pending_payment (window closed, network drop) can be paid
  // again while its hold lasts; the backend hands back a fresh checkout.
  const completePayment = async () => {
    setPaying(true);
    let out;
    try {
      out = await getOrderCheckout(order.id);
    } catch (err) {
      setPaying(false);
      if (err?.response?.data?.code === 'NOT_PAYABLE') setNotPayable(friendlyError(err));
      else toast.error(friendlyError(err, 'Payment could not be started'));
      return;
    }
    payShopOrder({
      order: out.order,
      checkout: out.checkout,
      initiatePayment,
      user,
      onPaid: (orderId) => {
        refreshCartCount();
        setPaying(false);
        navigate(`/shop/orders/${orderId}?placed=1`, { replace: true });
        load();
      },
      onVerifyError: (err) => {
        setPaying(false);
        toast.error(friendlyError(err, 'We could not confirm the payment. If money was taken it will be confirmed shortly.'));
        load();
      },
      onError: (msg) => {
        setPaying(false);
        toast.error(msg);
      },
      onDismiss: () => setPaying(false),
    });
  };

  const undoReturn = async (r) => {
    setUndoingReturn(r.id);
    try {
      await cancelReturn(r.id);
      toast.success('Return cancelled');
      await load();
    } catch (err) {
      toast.error(friendlyError(err, 'Could not cancel the return'));
    } finally {
      setUndoingReturn(null);
    }
  };

  // An unpaid order holds stock; cancelling releases it straight away.
  const releaseOrder = async () => {
    setReleasing(true);
    try {
      await cancelUnpaidOrder(order.id);
      toast.success('Order cancelled');
      setConfirmRelease(false);
      await load();
    } catch (err) {
      toast.error(friendlyError(err, 'Could not cancel the order'));
      load();
    } finally {
      setReleasing(false);
    }
  };

  const expired = order.status === 'expired';
  const unpaidCancelled = order.status === 'cancelled' && !order.paidAt;

  return (
    <ShopChrome>
      <div className="lk-container max-w-4xl py-5 sm:py-8">
        {placed && order.paidAt && !['pending_payment', 'expired'].includes(order.status) && (
          <div className="mb-5 overflow-hidden rounded-3xl bg-gradient-to-br from-success to-emerald-700 p-6 text-white shadow-cardHover sm:p-8">
            <PartyPopper className="h-10 w-10" />
            <h1 className="mt-3 text-h1 text-white">Order placed!</h1>
            <p className="mt-1 text-sm text-white/85">
              Thanks{user.name ? `, ${user.name.split(' ')[0]}` : ''}. We&apos;ve sent your order to {shipments.length > 1 ? `${shipments.length} stores` : 'the store'}.
              {shipments[0]?.expectedBy && <> Expected by <strong>{istDay(shipments[0].expectedBy)}</strong>.</>}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/shop" className="lk-btn bg-white text-ink-900 hover:bg-white/90">
                Continue shopping
              </Link>
              <Link to="/shop/orders" className="lk-btn border border-white/40 text-white hover:bg-white/10">
                All orders
              </Link>
            </div>
          </div>
        )}

        <Crumbs items={[{ name: 'Shop', to: '/shop' }, { name: 'My orders', to: '/shop/orders' }, { name: `#${order.orderNo}` }]} />

        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-h2 text-ink-900">Order #{order.orderNo}</h2>
            <p className="mt-0.5 text-sm text-ink-500">Placed on {istDateTime(order.createdAt)}</p>
          </div>
          <StatusPill tone={order.status === 'cancelled' || order.status === 'expired' ? 'danger' : ['pending_payment', 'partially_cancelled'].includes(order.status) ? 'warning' : 'success'}>
            {ORDER_LABELS[order.status] || order.status}
          </StatusPill>
        </div>

        {order.status === 'pending_payment' && (
          <div className="mt-4 flex items-start gap-3 rounded-2xl border border-warning/30 bg-warning-tint p-4 text-sm text-warning">
            <Clock className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p>
                We haven&apos;t received the payment yet. If money was taken, it will be confirmed automatically within a few minutes.
                {order.expiresAt && <> Items are held until {istDateTime(order.expiresAt)}.</>}
              </p>
              {countdown && countdown.left > 0 && (
                <p className="mt-1 font-semibold" aria-live="off">
                  Time left to pay: <span className="tabular" role="timer">{countdown.label}</span>
                </p>
              )}
            </div>
          </div>
        )}
        {order.status === 'pending_payment' && (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {notPayable ? (
              <>
                <p className="flex-1 text-sm font-medium text-danger">{notPayable}</p>
                <Link to="/shop/cart" className="lk-btn-primary">
                  Back to cart
                </Link>
              </>
            ) : confirmRelease ? (
              <>
                <p className="w-full text-sm font-medium text-ink-900 sm:w-auto sm:flex-1">Cancel this order? The items go back on sale.</p>
                <button type="button" onClick={() => setConfirmRelease(false)} disabled={releasing} className="lk-btn-ghost flex-1 sm:flex-none">
                  Keep order
                </button>
                <button type="button" onClick={releaseOrder} disabled={releasing} className="lk-btn flex-1 bg-danger text-white hover:bg-danger/90 sm:flex-none">
                  {releasing && <Loader2 className="h-4 w-4 animate-spin" />}
                  Cancel order
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={completePayment} disabled={paying || releasing} className="lk-btn-primary w-full sm:w-auto">
                  {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                  Complete payment · {rupees(order.total)}
                </button>
                <button type="button" onClick={() => setConfirmRelease(true)} disabled={paying || releasing} className="lk-btn-ghost w-full sm:w-auto">
                  <XCircle className="h-4 w-4" /> Cancel order
                </button>
              </>
            )}
          </div>
        )}
        {(expired || unpaidCancelled) && (
          <div className="mt-4 flex items-start gap-3 rounded-2xl border border-danger/20 bg-danger-tint p-4 text-sm text-danger">
            <XCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{expired ? 'Payment not completed — order expired' : 'Order cancelled before payment'}</p>
              <p className="mt-0.5 text-danger/80">No money was taken for this order and the items were released. You can order them again.</p>
            </div>
            <Link to="/shop/cart" className="lk-btn-primary shrink-0 self-center px-4 py-2 text-xs">
              Go to cart
            </Link>
          </div>
        )}

        <div className="mt-5 space-y-4">
          {shipments.map((s, si) => {
            const cancellable = canCancelOrder && CANCELLABLE_SHIPMENT.has(s.status) && s.items.some((i) => activeQty(i) > 0);
            return (
              <section key={s.id} className="lk-card overflow-hidden">
                <header className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-200/70 bg-surface-muted/60 px-5 py-3">
                  <p className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                    <Store className="h-4 w-4 text-ink-500" />
                    {shipments.length > 1 && <span className="text-ink-500">Shipment {si + 1} ·</span>}
                    <Link to={`/shop/store/${s.seller.slug}`} className="hover:underline">
                      {s.seller.name}
                    </Link>
                  </p>
                  {expired || unpaidCancelled ? (
                    <StatusPill tone="muted">Not paid</StatusPill>
                  ) : (
                    <StatusPill tone={shipmentTone(s.status)}>{SHIPMENT_LABELS[s.status] || s.status}</StatusPill>
                  )}
                </header>

                <div className="space-y-4 p-5">
                  {/* An unpaid order's shipments were never sent to the seller: no "Shipment cancelled" tracker. */}
                  {s.status !== 'awaiting_payment' && !expired && !unpaidCancelled && <ShipmentTracker shipment={s} />}

                  {s.status !== 'delivered' && s.status !== 'cancelled' && s.status !== 'returned_to_origin' && !expired && !unpaidCancelled && s.expectedBy && (
                    <p className="flex items-center gap-2 text-sm text-ink-700">
                      <Truck className="h-4 w-4 text-ink-500" />
                      Arriving by <span className="font-semibold text-ink-900">{istDay(s.expectedBy)}</span>
                    </p>
                  )}
                  {s.status === 'delivered' && s.deliveredAt && (
                    <p className="flex items-center gap-2 text-sm font-semibold text-success">
                      <CheckCircle2 className="h-4 w-4" /> Delivered on {istDateTime(s.deliveredAt)}
                    </p>
                  )}
                  {s.delivery && !expired && !unpaidCancelled && <DeliveryCard orderId={order.id} shipment={s} onStatusChange={load} />}
                  {s.awb && s.deliveryProvider !== 'kuicko' && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl bg-surface-muted px-4 py-3 text-sm">
                      <span className="text-ink-500">
                        Courier <span className="font-semibold text-ink-900">{s.courier || '—'}</span>
                      </span>
                      <span className="tabular text-ink-500">
                        AWB <span className="font-semibold text-ink-900">{s.awb}</span>
                      </span>
                      {s.trackingUrl && (
                        <a href={s.trackingUrl} target="_blank" rel="noopener noreferrer" className="ml-auto inline-flex items-center gap-1 font-semibold text-brand-secondary hover:underline">
                          Track package <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  )}

                  <ul className="divide-y divide-ink-200/70 rounded-2xl border border-ink-200/70">
                    {s.items.map((i) => {
                      const live = activeQty(i);
                      return (
                        <li key={i.id} className="p-4">
                          <div className="flex gap-3">
                            {i.productSlug ? (
                              <Link to={`/shop/p/${i.productSlug}?variant=${i.variantId}`} className="lk-focus shrink-0 rounded-xl">
                                <Img src={i.image} alt={i.title} className="h-24 w-[72px] rounded-xl" />
                              </Link>
                            ) : (
                              <Img src={i.image} alt={i.title} className="h-24 w-[72px] shrink-0 rounded-xl" />
                            )}
                            <div className="min-w-0 flex-1 text-sm">
                              {i.productSlug ? (
                                <Link to={`/shop/p/${i.productSlug}?variant=${i.variantId}`} className="lk-focus line-clamp-2 rounded font-medium text-ink-900 hover:underline">
                                  {i.title}
                                </Link>
                              ) : (
                                <p className="line-clamp-2 font-medium text-ink-900">{i.title}</p>
                              )}
                              <p className="text-xs text-ink-500">
                                {i.variantLabel} · Qty {i.qty}
                              </p>
                              <p className="tabular mt-1">
                                <span className="font-bold text-ink-900">{rupees(i.lineTotal)}</span>
                                {i.mrp > i.unitPrice && <span className="ml-2 text-xs text-ink-400 line-through">{rupees(i.mrp * i.qty)}</span>}
                              </p>
                              <div className="mt-1 flex flex-wrap gap-1.5">
                                {i.cancelledQty > 0 && <StatusPill tone="danger">{i.cancelledQty === i.qty ? 'Cancelled' : `${i.cancelledQty} cancelled`}</StatusPill>}
                                {i.returnedQty > 0 && <StatusPill tone="muted">{i.returnedQty} returned</StatusPill>}
                                {i.refundedAmount > 0 && <StatusPill tone="success">{rupees(i.refundedAmount)} refunded</StatusPill>}
                              </div>
                            </div>
                          </div>

                          {/* Returns on this line */}
                          {i.returns?.map((r) => (
                            <div key={r.id} className="mt-3 rounded-xl border border-ink-200/70 bg-surface-muted/60 p-3 text-sm">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className="flex items-center gap-2 font-semibold text-ink-900">
                                  <RotateCcw className="h-4 w-4 text-ink-500" />
                                  {RETURN_LABELS[r.status] || r.status}
                                  <span className="font-normal text-ink-500">· {r.qty} item{r.qty > 1 ? 's' : ''}</span>
                                </span>
                                {r.status === 'requested' && (
                                  <button
                                    type="button"
                                    onClick={() => undoReturn(r)}
                                    disabled={undoingReturn === r.id}
                                    className="lk-focus inline-flex items-center gap-1 rounded text-xs font-semibold text-danger hover:underline disabled:opacity-60"
                                  >
                                    {undoingReturn === r.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                    {undoingReturn === r.id ? 'Cancelling…' : 'Cancel return'}
                                  </button>
                                )}
                              </div>
                              <p className="mt-0.5 text-xs text-ink-500">
                                {reasonLabel(r.reason)} · requested {istDate(r.createdAt)}
                                {Number(r.refundAmount) > 0 && <> · refund {rupees(r.refundAmount)}</>}
                              </p>
                              {r.resolutionNote && <p className="mt-1 text-xs text-ink-700">Seller: {r.resolutionNote}</p>}
                            </div>
                          ))}

                          {/* Actions */}
                          <div className="mt-3 flex flex-wrap gap-2">
                            {i.return?.returnable && (
                              <button type="button" onClick={() => setReturnFor(i)} className="lk-btn-ghost min-h-[40px] px-4 py-2">
                                <RotateCcw className="h-4 w-4" /> Return
                                <span className="text-xs font-normal text-ink-500">({i.return.daysLeft}d left)</span>
                              </button>
                            )}
                            {s.status === 'delivered' && live > 0 && (
                              <button type="button" onClick={() => setReviewFor(i)} className="lk-btn-ghost min-h-[40px] px-4 py-2">
                                <Star className={`h-4 w-4 ${i.review || i.reviewed ? 'fill-amber-400 text-amber-400' : ''}`} />
                                {i.review || i.reviewed ? 'Edit review' : 'Rate & review'}
                              </button>
                            )}
                            {s.status === 'delivered' && !i.return?.returnable && i.returnWindowDays === 0 && (
                              <span className="self-center text-xs text-ink-500">Not returnable</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  {cancellable && (
                    <button type="button" onClick={() => setCancelFor(s)} className="lk-focus inline-flex items-center gap-1.5 rounded text-sm font-semibold text-danger hover:underline">
                      <XCircle className="h-4 w-4" /> Cancel items from this shipment
                    </button>
                  )}
                </div>
              </section>
            );
          })}

          <div className="grid gap-4 md:grid-cols-2">
            {address && (
              <section className="lk-card p-5">
                <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-ink-500">
                  <MapPin className="h-4 w-4" /> Delivery address
                </h3>
                <p className="mt-2 text-sm font-semibold text-ink-900">{address.name}</p>
                <p className="text-sm text-ink-700">{[address.line, address.landmark, address.city, address.state].filter(Boolean).join(', ')}</p>
                <p className="tabular text-sm text-ink-700">PIN {address.pincode}</p>
                {address.phone && <p className="tabular mt-1 text-sm text-ink-500">Phone {address.phone}</p>}
              </section>
            )}

            <section className="lk-card p-5">
              <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-ink-500">
                <ReceiptIndianRupee className="h-4 w-4" /> Payment
              </h3>
              <dl className="tabular mt-2 space-y-2 text-sm">
                {summary?.totalMrp > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-ink-700">Total MRP</dt>
                    <dd>{rupees(summary.totalMrp)}</dd>
                  </div>
                )}
                {summary?.discountOnMrp > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-ink-700">Discount</dt>
                    <dd className="text-success">− {rupees(summary.discountOnMrp)}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-ink-700">Delivery</dt>
                  <dd>{fees ? rupees(fees) : <span className="text-success">Free</span>}</dd>
                </div>
                <div className="flex justify-between border-t border-dashed border-ink-200 pt-2 font-bold text-ink-900">
                  <dt>Order total</dt>
                  <dd>{rupees(order.total)}</dd>
                </div>
                {order.amountPaid > 0 && (
                  <div className="flex justify-between text-ink-700">
                    <dt>Paid online</dt>
                    <dd>{rupees(order.amountPaid)}</dd>
                  </div>
                )}
                {order.amountRefunded > 0 && (
                  <div className="flex justify-between font-semibold text-success">
                    <dt>Refunded</dt>
                    <dd>{rupees(order.amountRefunded)}</dd>
                  </div>
                )}
              </dl>
              {refunds.length > 0 && (
                <ul className="mt-3 space-y-1.5 border-t border-ink-200/70 pt-3 text-xs text-ink-500">
                  {refunds.map((r) => (
                    <li key={r.id} className="flex justify-between gap-2">
                      <span>
                        Refund · {istDate(r.createdAt)}
                        {r.status && <span className="ml-1 capitalize">({String(r.status).replace(/_/g, ' ')})</span>}
                      </span>
                      <span className="tabular font-semibold text-ink-700">{rupees(r.amount)}</span>
                    </li>
                  ))}
                  <li className="pt-1">Refunds reach your original payment method in 5–7 working days.</li>
                </ul>
              )}
            </section>
          </div>
        </div>
      </div>

      <CancelSheet open={Boolean(cancelFor)} onClose={() => setCancelFor(null)} orderId={order.id} items={cancelFor?.items || []} onDone={load} />
      <ReturnSheet open={Boolean(returnFor)} onClose={() => setReturnFor(null)} item={returnFor} onDone={load} />
      <ReviewSheet open={Boolean(reviewFor)} onClose={() => setReviewFor(null)} item={reviewFor} onDone={load} />
    </ShopChrome>
  );
};

export default ShopOrderDetail;
